<template>
  <div class="dashboard-container px-2 py-4">
    <!-- Dashboard Header with Time filter state -->
    <dashboard-header
      v-model="selectedFilter"
      :disabled="isLoading"
    />

    <!-- Section 1: Points Statistics & Accounts Line Chart -->
    <v-row
      class="mt-3"
      dense
    >
      <v-col
        cols="12"
        sm="6"
        md="3"
        class="mb-4 d-flex"
      >
        <summary-card
          title="Total Points Earned"
          :value="pointsEarned"
          :loading="isLoading"
          class="flex-grow-1"
        />
      </v-col>

      <v-col
        cols="12"
        sm="6"
        md="3"
        class="mb-4 d-flex"
      >
        <summary-card
          title="Total Points Redeemed"
          :value="pointsRedeemed"
          :loading="isLoading"
          class="flex-grow-1"
        />
      </v-col>

      <v-col
        cols="12"
        md="6"
        class="mb-4 d-flex"
      >
        <accounts-chart
          v-model="selectedFilter"
          :chart-data="customerStats"
          :loading="isLoading"
          class="flex-grow-1"
          @show-notification="showNotification"
        />
      </v-col>
    </v-row>

    <!-- Section 2: Target Achievers & Most Repeated Customers -->
    <v-row dense>
      <v-col
        cols="12"
        lg="6"
        class="mb-4 d-flex"
      >
        <dashboard-table-card
          title="Monthly Target Achievers"
          :headers="headers.targetAchievers"
          :rows="data.targetAchievers"
          :loading="isLoading"
          initial-outlet="Salmiya"
          :initial-filter="selectedFilter"
          class="flex-grow-1"
          @show-notification="showNotification"
        />
      </v-col>

      <v-col
        cols="12"
        lg="6"
        class="mb-4 d-flex"
      >
        <dashboard-table-card
          title="Most Repeated Customers"
          :headers="headers.repeatedCustomers"
          :rows="data.repeatedCustomers"
          :loading="isLoading"
          initial-outlet="Salmiya"
          :initial-filter="selectedFilter"
          class="flex-grow-1"
          @show-notification="showNotification"
        />
      </v-col>
    </v-row>

    <!-- Section 3: Upcoming Birthdays & Most Spending Customers -->
    <v-row dense>
      <v-col
        cols="12"
        lg="6"
        class="mb-4 d-flex"
      >
        <dashboard-table-card
          title="Upcoming Birthdays list"
          :headers="headers.birthdays"
          :rows="data.birthdays"
          :loading="isLoading"
          initial-outlet="Salmiya"
          :initial-filter="selectedFilter"
          class="flex-grow-1"
          @show-notification="showNotification"
        />
      </v-col>

      <v-col
        cols="12"
        lg="6"
        class="mb-4 d-flex"
      >
        <dashboard-table-card
          title="Most Spending Customers"
          :headers="headers.spendingCustomers"
          :rows="data.spendingCustomers"
          :loading="isLoading"
          initial-outlet="Salmiya"
          :initial-filter="selectedFilter"
          class="flex-grow-1"
          @show-notification="showNotification"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { ref, onMounted, watch } from '@vue/composition-api'
import store from '@/store'
import dashboard from '@/apis/dashboard'

// Dashboard Components
import DashboardHeader from './dashboard/components/DashboardHeader.vue'
import SummaryCard from './dashboard/components/SummaryCard.vue'
import AccountsChart from './dashboard/components/AccountsChart.vue'
import DashboardTableCard from './dashboard/components/DashboardTableCard.vue'

export default {
	name: 'HomeDashboard',
	components: {
		DashboardHeader,
		SummaryCard,
		AccountsChart,
		DashboardTableCard,
	},
	setup() {
		const selectedFilter = ref('Monthly')
		const pointsEarned = ref('0')
		const pointsRedeemed = ref('0')
		const customerStats = ref({ active: [], inactive: [] })
		const data = ref({
			targetAchievers: [],
			repeatedCustomers: [],
			birthdays: [],
			spendingCustomers: [],
		})
		const isLoading = ref(false)

		// Table headers matching the design template columns
		const headers = {
			targetAchievers: ['Name', 'Mobile', 'Points', 'Amount (KD)'],
			repeatedCustomers: ['Name', 'Mobile', 'Points', 'Count'],
			birthdays: ['Name', 'Mobile', 'Points', 'Date'],
			spendingCustomers: ['Name', 'Transaction Count', 'Points'],
		}

		// Coordinates snackbar notifications using the Vuex store mutation
		const showNotification = payload => {
			if (payload.type === 'success') {
				store.commit('app/SET_SNACKBAR_SUCCESS', payload.message)
			} else if (payload.type === 'error') {
				store.commit('app/SET_SNACKBAR_ERROR', payload.message)
			} else if (payload.type === 'warning') {
				store.commit('app/SET_SNACKBAR_WARNING', payload.message)
			} else {
				store.commit('app/SET_SNACKBAR_INFO', payload.message)
			}
		}

		const updateDashboard = () => {
			isLoading.value = true
			const params = { filter: selectedFilter.value }

			Promise.all([
				dashboard.getTotalPoints(params),
				dashboard.getActiveInactiveCustomers(params),
				dashboard.getMonthlyTargetAchievers(params),
				dashboard.getMostRepeatedCustomers(params),
				dashboard.getMostSpendingCustomers(params),
				dashboard.getUpcomingBirthdays(),
			])
				.then(([pointsRes, chartRes, targetRes, repeatedRes, spendingRes, birthdayRes]) => {
					const earned = pointsRes.data?.data?.total_points_earned || 0
					const redeemed = pointsRes.data?.data?.total_points_redeemed || 0
					pointsEarned.value = earned.toLocaleString()
					pointsRedeemed.value = redeemed.toLocaleString()

					customerStats.value = chartRes.data?.data || { active: [], inactive: [] }

					data.value = {
						targetAchievers: targetRes.data?.data || [],
						repeatedCustomers: repeatedRes.data?.data || [],
						spendingCustomers: spendingRes.data?.data || [],
						birthdays: birthdayRes.data?.data || [],
					}
				})
				.catch(error => {
					showNotification({
						message: `Failed to load dashboard analytics: ${error.response?.data?.message || error.message}`,
						type: 'error',
					})
				})
				.finally(() => {
					isLoading.value = false
				})
		}

		onMounted(() => {
			updateDashboard()
		})

		watch(selectedFilter, () => {
			updateDashboard()
		})

		return {
			selectedFilter,
			pointsEarned,
			pointsRedeemed,
			customerStats,
			headers,
			data,
			isLoading,
			showNotification,
		}
	},
}
</script>

<style lang="scss" scoped>
.dashboard-container {
	background-color: #f9f9f9;
	min-height: calc(100vh - 120px);
	border-radius: 8px;
}
</style>
